import React, { useState } from "react";
import { Link } from "react-router-dom";
import { doc, getDoc, setDoc, Timestamp } from "firebase/firestore";
import db from "./Firebase";

const compactPhone = (val) => String(val || "").replace(/\D/g, "").slice(-10);

const MEMBERSHIP_TYPES = [
    { type: "shiksha",              tag: "shiksha",      label: "Shiksha (₹499)",            defaultPrice: 499  },
    { type: "shiksha_plus_monthly", tag: "shiksha_plus", label: "Shiksha Plus Monthly (₹199)", defaultPrice: 199 },
    { type: "shiksha_plus_yearly",  tag: "shiksha_plus", label: "Shiksha Plus Yearly (₹1999)", defaultPrice: 1999 },
];

const todayStr = () => new Date().toISOString().slice(0, 10);

const toIST = (dateStr, endOfDay = false) => {
    const time = endOfDay ? "T00:00:00" : "T00:00:00";
    return Timestamp.fromDate(new Date(dateStr + time + "+05:30"));
};

export default function AddShikshaSubscription() {
    const [form, setForm] = useState({
        name: "",
        phone: "",
        membershipType: "shiksha",
        tag: "shiksha",
        price: "499",
        startDate: todayStr(),
        endDate: "",
    });
    const [submitStatus, setSubmitStatus] = useState(null); // null | "loading" | "created" | "updated" | "error"
    const [errorMsg, setErrorMsg] = useState("");
    const [existingData, setExistingData] = useState(null); // preview of existing doc if phone found

    const handleMembershipChange = (e) => {
        const selected = MEMBERSHIP_TYPES.find(m => m.type === e.target.value);
        if (selected) {
            setForm(f => ({ ...f, membershipType: selected.type, tag: selected.tag, price: String(selected.defaultPrice) }));
        }
    };

    const handlePhoneBlur = async () => {
        const phone = compactPhone(form.phone);
        if (phone.length !== 10) { setExistingData(null); return; }
        try {
            const snap = await getDoc(doc(db, "nv_shiksha_subscriptions", phone));
            if (snap.exists()) {
                const d = snap.data();
                setExistingData({
                    name: d.name || "-",
                    membershipType: d.membership_type || "-",
                    logCount: Array.isArray(d.subscription_log) ? d.subscription_log.length : 0,
                });
            } else {
                setExistingData(null);
            }
        } catch (_) {
            setExistingData(null);
        }
    };

    const handleSubmit = async () => {
        const phone = compactPhone(form.phone);
        if (phone.length !== 10) { setErrorMsg("Valid 10-digit phone number required."); return; }
        if (!form.name.trim())    { setErrorMsg("Name is required."); return; }
        if (!form.startDate)      { setErrorMsg("Start date is required."); return; }
        if (!form.endDate)        { setErrorMsg("End date is required."); return; }

        setSubmitStatus("loading");
        setErrorMsg("");
        try {
            const nowTs    = Timestamp.now();
            const startTs  = toIST(form.startDate);
            const endTs    = toIST(form.endDate);
            const price    = parseInt(form.price) || 0;

            const logEntry = {
                created_date:    nowTs,
                membership_type: form.membershipType,
                price,
            };

            const docRef  = doc(db, "nv_shiksha_subscriptions", phone);
            const docSnap = await getDoc(docRef);

            const isNew = !docSnap.exists();
            const existingLog = isNew
                ? []
                : (Array.isArray(docSnap.data().subscription_log) ? docSnap.data().subscription_log : []);

            // Full overwrite for both new and existing — preserves original created_date if exists
            await setDoc(docRef, {
                created_date:    isNew ? nowTs : (docSnap.data().created_date || nowTs),
                name:            form.name.trim(),
                phone_no:        phone,
                membership_type: form.membershipType,
                price,
                tag:             form.tag,
                start_date:      startTs,
                end_date:        endTs,
                status:          "active",
                subscription_log: [...existingLog, logEntry],
            });

            setSubmitStatus(isNew ? "created" : "updated");
            setExistingData(null);
        } catch (err) {
            console.error("AddShikshaSubscription error:", err);
            setErrorMsg(err.message || "Something went wrong.");
            setSubmitStatus("error");
        }
    };

    const handleReset = () => {
        setForm({ name: "", phone: "", membershipType: "shiksha", tag: "shiksha", price: "499", startDate: todayStr(), endDate: "" });
        setSubmitStatus(null);
        setErrorMsg("");
        setExistingData(null);
    };

    return (
        <div className="container-fluid py-4" style={{ maxWidth: "580px" }}>

            {/* HEADER */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h3 className="mb-1">Add Shiksha Subscription</h3>
                    <div className="text-muted" style={{ fontSize: "13px" }}>
                        Creates or updates record in <code>nv_shiksha_subscriptions</code>
                    </div>
                </div>
                <Link to="/" className="btn btn-outline-secondary btn-sm">Back</Link>
            </div>

            <div className="card border-0 shadow-sm">
                <div className="card-body p-4">

                    {/* NAME */}
                    <div className="mb-3">
                        <div style={{ fontSize: "13px", fontWeight: 600, marginBottom: "6px" }}>Name</div>
                        <input
                            type="text"
                            className="form-control"
                            placeholder="Full name"
                            value={form.name}
                            onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                        />
                    </div>

                    {/* PHONE */}
                    <div className="mb-3">
                        <div style={{ fontSize: "13px", fontWeight: 600, marginBottom: "6px" }}>Phone No</div>
                        <input
                            type="tel"
                            className="form-control"
                            placeholder="10-digit phone number (document ID)"
                            value={form.phone}
                            onChange={e => { setForm(f => ({ ...f, phone: e.target.value })); setExistingData(null); }}
                            onBlur={handlePhoneBlur}
                        />
                        {existingData && (
                            <div className="alert alert-warning py-2 mt-2 mb-0" style={{ fontSize: "12px" }}>
                                <strong>⚠ Existing record found</strong> — Name: <strong>{existingData.name}</strong> ·
                                Plan: <strong>{existingData.membershipType}</strong> ·
                                Log entries: <strong>{existingData.logCount}</strong>.
                                Submitting will <u>update</u> this record and append a new log entry.
                            </div>
                        )}
                    </div>

                    {/* MEMBERSHIP TYPE */}
                    <div className="mb-3">
                        <div style={{ fontSize: "13px", fontWeight: 600, marginBottom: "6px" }}>Membership Type</div>
                        <select
                            className="form-select"
                            value={form.membershipType}
                            onChange={handleMembershipChange}
                        >
                            {MEMBERSHIP_TYPES.map(m => (
                                <option key={m.type} value={m.type}>{m.label}</option>
                            ))}
                        </select>
                    </div>

                    {/* PRICE + TAG */}
                    <div className="row g-3 mb-3">
                        <div className="col-6">
                            <div style={{ fontSize: "13px", fontWeight: 600, marginBottom: "6px" }}>Price (₹)</div>
                            <input
                                type="number"
                                className="form-control"
                                value={form.price}
                                min="0"
                                onChange={e => setForm(f => ({ ...f, price: e.target.value }))}
                            />
                        </div>
                        <div className="col-6">
                            <div style={{ fontSize: "13px", fontWeight: 600, marginBottom: "6px" }}>Tag</div>
                            <input
                                type="text"
                                className="form-control"
                                value={form.tag}
                                onChange={e => setForm(f => ({ ...f, tag: e.target.value }))}
                            />
                        </div>
                    </div>

                    {/* START + END DATE */}
                    <div className="row g-3 mb-4">
                        <div className="col-6">
                            <div style={{ fontSize: "13px", fontWeight: 600, marginBottom: "6px" }}>Start Date</div>
                            <input
                                type="date"
                                className="form-control"
                                value={form.startDate}
                                onChange={e => setForm(f => ({ ...f, startDate: e.target.value }))}
                            />
                        </div>
                        <div className="col-6">
                            <div style={{ fontSize: "13px", fontWeight: 600, marginBottom: "6px" }}>End Date</div>
                            <input
                                type="date"
                                className="form-control"
                                value={form.endDate}
                                onChange={e => setForm(f => ({ ...f, endDate: e.target.value }))}
                            />
                        </div>
                    </div>

                    {/* PREVIEW */}
                    {form.phone && form.name && form.endDate && (
                        <div className="rounded p-3 mb-3" style={{ background: "#f8fafc", border: "1px solid #e2e8f0", fontSize: "12px" }}>
                            <div className="fw-bold mb-1" style={{ fontSize: "13px" }}>Preview</div>
                            <div><span className="text-muted">Document ID:</span> <strong>{compactPhone(form.phone) || "-"}</strong></div>
                            <div><span className="text-muted">Name:</span> <strong>{form.name}</strong></div>
                            <div><span className="text-muted">Membership:</span> <strong>{form.membershipType}</strong> · Tag: <strong>{form.tag}</strong> · ₹<strong>{form.price}</strong></div>
                            <div><span className="text-muted">Period:</span> <strong>{form.startDate}</strong> → <strong>{form.endDate}</strong></div>
                            <div><span className="text-muted">Status:</span> <strong>active</strong></div>
                        </div>
                    )}

                    {/* ERROR */}
                    {errorMsg && (
                        <div className="alert alert-danger py-2 mb-3" style={{ fontSize: "13px" }}>{errorMsg}</div>
                    )}

                    {/* SUCCESS */}
                    {(submitStatus === "created" || submitStatus === "updated") && (
                        <div className="alert alert-success py-2 mb-3" style={{ fontSize: "13px" }}>
                            {submitStatus === "created"
                                ? "✓ New subscription created successfully!"
                                : "✓ Subscription updated & new log entry appended!"}
                        </div>
                    )}

                    {/* BUTTONS */}
                    <div className="d-flex gap-2">
                        <button
                            className="btn btn-primary px-4"
                            style={{ fontWeight: 700 }}
                            disabled={submitStatus === "loading"}
                            onClick={handleSubmit}
                        >
                            {submitStatus === "loading"
                                ? <><span className="spinner-border spinner-border-sm me-2" />Saving…</>
                                : existingData ? "Update Subscription" : "Create Subscription"}
                        </button>
                        {(submitStatus === "created" || submitStatus === "updated") && (
                            <button className="btn btn-outline-secondary" onClick={handleReset}>
                                Add Another
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
